import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { ClassCard } from "@/components/ClassCard";
import { InterestModal } from "@/components/InterestModal";
import { TestimonialCarousel } from "@/components/TestimonialCarousel";
import { BookButton } from "@/components/PrimaryCTA";
import { api } from "@/lib/apiClient";
import { CLASSES_INTRO, CLASS_IMAGES, IMAGES } from "@/content";

function FeaturedOpen({ cls, onInterest }) {
  if (!cls) return null;
  return (
    <Reveal>
      <div data-testid="featured-open" className="relative overflow-hidden rounded-3xl border border-white/10 glass p-8 sm:p-12 glow-blue">
        <div className="pointer-events-none absolute inset-0">
          <img src={CLASS_IMAGES.OPEN} alt="" className="faded-media h-full w-full object-cover" loading="lazy" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(120deg, rgba(12,44,104,0.82) 0%, rgba(12,44,104,0.5) 45%, ${cls.accent}22 100%)` }} />
        </div>
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full blur-3xl" style={{ background: `${cls.accent}55` }} />
        <div className="relative grid lg:grid-cols-2 gap-10">
          <div>
            <span className="label" style={{ color: cls.accent }}>{cls.tagline}</span>
            <h2 className="font-display text-5xl sm:text-6xl font-black text-white mt-3">{cls.name}</h2>
            <p className="text-ice/80 mt-5 leading-relaxed">{cls.description}</p>
            <p className="text-ice/65 mt-4 leading-relaxed whitespace-pre-line">{cls.extra}</p>
            <div className="mt-7">
              <p className="label mb-3">You'll leave feeling</p>
              <div className="flex flex-wrap gap-3">
                {cls.leave_feeling.map((f) => (
                  <span key={f} className="rounded-full border border-white/15 px-4 py-1.5 text-sm text-ice capitalize">{f}</span>
                ))}
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-4 items-center">
              <BookButton />
              <span className="text-mist text-sm">Also bookable as a gym / studio taster session</span>
            </div>
          </div>
          <div>
            <p className="label mb-4">Trains</p>
            <div className="grid grid-cols-2 gap-3">
              {cls.emphasis.map((e) => (
                <div key={e} className="flex items-center gap-2 rounded-xl bg-ink-800/70 border border-white/8 px-4 py-3">
                  <Check size={15} style={{ color: cls.accent }} />
                  <span className="text-ice/85 text-sm capitalize">{e}</span>
                </div>
              ))}
            </div>
            {cls.testimonials?.length > 0 && (
              <div className="mt-8 rounded-2xl bg-ink-900/60 border border-white/8 p-6">
                <TestimonialCarousel items={cls.testimonials} testId="open-testimonials" auto={false} />
              </div>
            )}
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function Classes() {
  const [classes, setClasses] = useState([]);
  const [interest, setInterest] = useState(null);

  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); }, []);

  const open = classes.find((c) => c.slug === "OPEN");
  const rest = classes.filter((c) => c.slug !== "OPEN");

  return (
    <Layout page="classes">
      <section className="relative pt-40 pb-16 overflow-hidden">
        <img src={IMAGES.classesHeader} alt="Movement class" className="absolute inset-0 h-full w-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/70 via-ink-900/45 to-ink-900" />
        <div className="absolute inset-0 mesh-top opacity-60" />
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
          <span className="label">The strands</span>
          <h1 className="font-display font-black text-white tracking-tighter mt-4 leading-[0.95]" style={{ fontSize: "clamp(3rem, 10vw, 8rem)" }}>Classes</h1>
          <p className="text-ice/80 text-lg sm:text-xl max-w-3xl mt-7 leading-relaxed">{CLASSES_INTRO}</p>
        </div>
      </section>

      <section className="relative py-10">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8"><FeaturedOpen cls={open} onInterest={setInterest} /></div>
      </section>

      <section className="relative py-14">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal><h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-8">The individual strands</h2></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-fr">
            {rest.map((c) => (
              <div id={c.slug} key={c.slug} className="scroll-mt-28">
                <ClassCard cls={c} onInterest={setInterest} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 text-center">
        <Reveal>
          <Link to="/book" data-testid="classes-book-link"
            className="group inline-flex items-center gap-3 font-display text-3xl sm:text-4xl font-bold text-white hover:text-cyan transition-colors">
            Book a class <ArrowRight className="group-hover:translate-x-2 transition-transform" size={30} />
          </Link>
        </Reveal>
      </section>

      <InterestModal cls={interest} onClose={() => setInterest(null)} />
    </Layout>
  );
}
